import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Info, ThumbsDown, ThumbsUp } from 'lucide-react';
import React, { useContext, useEffect, useState } from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { Circles } from 'react-loading-icons';
import { Rating } from 'react-simple-star-rating';
import { Toaster, toast } from 'sonner';
import * as z from 'zod';

import { ReactComponent as StarsEmpty } from '../../../assets/images/StarSVGEmpty.svg';
import { ReactComponent as StarsFilled } from '../../../assets/images/StarSVGFilled.svg';
import {
  handleEducationAddition,
  handleEntertainmentAddition,
  handleMyProductAddition,
} from '../../../util/DatabaseUtil';
import { visualRating } from '../../../util/EntertainmentUtil';
import { ContextQuestions } from '../../rating/ContextQuestions';
import { RatingContext } from '../RatingPageTabs';

const ratingSchema = z.object({
  tags: z.string().optional(),
  isRecommended: z.boolean({
    required_error: 'Please specify if you recommend this.',
    invalid_type_error: 'Please specify if you recommend this.',
  }),
  ratings: z
    .array(
      z.object({
        questionId: z.string(),
        questionTitle: z.string(),
        description: z.string().optional(),
        rating: z.number().min(0).max(10),
      }),
    )
    .min(1, 'There must be at least one rating question.'),
});

function UserRating({ reviewSubmitted, setRefresh }) {
  const [contextQuestionsVerified, setContextQuestionsVerified] =
    useState(false);
  const { formData, setFormData, id, cat } = useContext(RatingContext);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(ratingSchema),
    defaultValues: {
      isRecommended: formData?.isRecommended ?? undefined,
      tags: formData?.tags || '',
      ratings: formData?.ratings || [],
    },
  });

  const { fields } = useFieldArray({
    control,
    name: 'ratings',
  });

  // Sync formData.ratings to RHF when loaded
  useEffect(() => {
    if (formData?.ratings?.length > 0 && fields.length === 0) {
      setValue('ratings', formData.ratings);
    }
  }, [formData?.ratings, fields.length, setValue]);

  const updateFormData = React.useCallback(
    (receivedRating) => {
      setFormData((prev) => ({ ...prev, ...receivedRating }));
    },
    [setFormData],
  );

  const checkContextFill = React.useCallback((result) => {
    setContextQuestionsVerified(result);
  }, []);

  const onSubmit = async (data) => {
    if (!contextQuestionsVerified) {
      toast.error(
        'Please fill in all required context fields (marked with *).',
      );
      return;
    }

    const sendToRatings = { ...formData, ...data };

    try {
      if (id) {
        await handleEntertainmentAddition(sendToRatings);
      } else {
        await handleEducationAddition(sendToRatings);
      }

      try {
        await handleMyProductAddition(sendToRatings);
      } catch (err) {
        console.error('Error Send My Products Data', err);
      }

      toast.success('Rating submitted successfully!');

      // small delay for UX before switching tabs
      setTimeout(() => {
        reviewSubmitted();
        setRefresh(true);
      }, 1000);
    } catch (err) {
      console.error('Error Sending to Ratings', err);
      toast.error('Failed to submit rating. Please try again.');
    }
  };

  const currentIsRecommended = watch('isRecommended');

  // Helper toggle description
  const [openDescIds, setOpenDescIds] = useState([]);
  const toggleDescription = (qId) => {
    setOpenDescIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId],
    );
  };

  return (
    <>
      <Toaster theme="dark" position="top-right" richColors />
      {formData && formData.ratings && formData.ratings.length > 0 ? (
        <div className="mx-4 md:mx-10 lg:mx-20 my-10 bg-[#081325] rounded-3xl p-6 md:p-10 shadow-2xl border border-slate-800 text-white font-montserrat">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-12">
            {/* Context Questions */}
            <div className="bg-[#111827] rounded-3xl p-6 border border-slate-800/60 shadow-inner">
              <ContextQuestions
                cat={cat}
                updateRating={updateFormData}
                checkContext={checkContextFill}
                formData={formData}
              />
            </div>

            {/* Rating Grid */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-slate-100 mb-6 border-b border-slate-800 pb-2">
                Rate Specific Categories
              </h3>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 w-full">
                {fields.map((field, index) => {
                  const ratingVal = watch(`ratings.${index}.rating`) || 0;
                  const currentScore = ratingVal / 2;
                  const descOpen = openDescIds.includes(field.questionId);

                  return (
                    <div
                      key={field.id}
                      className="bg-[#111827] border border-slate-700/50 rounded-2xl p-5 hover:border-slate-600 transition-all shadow-lg hover:shadow-xl group"
                    >
                      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 justify-between">
                        {/* Score Indicator */}
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold shadow-inner ${visualRating(
                            currentScore,
                          )} text-white shrink-0 transition-colors duration-300`}
                        >
                          {currentScore}
                        </div>

                        {/* Title & Info Toggle */}
                        <div className="flex-1 text-center sm:text-left">
                          <label className="text-slate-200 font-semibold mb-1 block">
                            {field.questionTitle}
                          </label>
                          <button
                            type="button"
                            onClick={() => toggleDescription(field.questionId)}
                            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 justify-center sm:justify-start transition-colors"
                          >
                            <Info className="w-3.5 h-3.5" />
                            {descOpen ? 'Hide Info' : 'Show Info'}
                          </button>
                        </div>

                        {/* Star Rating controller */}
                        <div className="shrink-0 scale-90 sm:scale-100 origin-right">
                          <Controller
                            control={control}
                            name={`ratings.${index}.rating`}
                            render={({ field: { onChange, value } }) => (
                              <Rating
                                onClick={(rate) => {
                                  onChange(rate * 2);
                                }}
                                initialValue={(value || 0) / 2}
                                allowFraction
                                emptyIcon={
                                  <StarsEmpty className="inline-block" />
                                }
                                fillIcon={
                                  <StarsFilled className="inline-block drop-shadow-md" />
                                }
                                className="group-hover:scale-105 transition-transform"
                                SVGclassName="inline-block"
                                SVGstorkeWidth={0}
                              />
                            )}
                          />
                        </div>
                      </div>

                      {/* Description Dropdown */}
                      {descOpen && field.description && (
                        <div className="mt-4 text-sm text-slate-400 bg-slate-900/50 p-4 rounded-xl border border-slate-800 animate-in slide-in-from-top-2 fade-in">
                          {field.description}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recommendation & Tags */}
            <div className="bg-[#111827] rounded-3xl p-8 border border-slate-800 shadow-lg mt-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Recommend */}
                <div className="flex flex-col items-center justify-center space-y-6">
                  <div className="text-center">
                    <label className="text-xl font-bold text-slate-100 flex items-center justify-center gap-2">
                      Do you recommend this?{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <p className="text-slate-400 text-sm mt-1">
                      Would you suggest this to others?
                    </p>
                  </div>

                  <div className="flex items-center gap-6">
                    <button
                      type="button"
                      onClick={() =>
                        setValue('isRecommended', true, {
                          shouldValidate: true,
                        })
                      }
                      className={`flex flex-col items-center gap-2 p-4 w-32 rounded-2xl border-2 transition-all duration-300 ${
                        currentIsRecommended === true
                          ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400 scale-105 shadow-lg shadow-emerald-500/20'
                          : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                      }`}
                    >
                      <ThumbsUp
                        className={`w-8 h-8 ${currentIsRecommended === true ? 'fill-emerald-500/20' : ''}`}
                      />
                      <span className="font-semibold">Yes</span>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setValue('isRecommended', false, {
                          shouldValidate: true,
                        })
                      }
                      className={`flex flex-col items-center gap-2 p-4 w-32 rounded-2xl border-2 transition-all duration-300 ${
                        currentIsRecommended === false
                          ? 'border-red-500 bg-red-500/10 text-red-400 scale-105 shadow-lg shadow-red-500/20'
                          : 'border-slate-700 bg-slate-800 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                      }`}
                    >
                      <ThumbsDown
                        className={`w-8 h-8 ${currentIsRecommended === false ? 'fill-red-500/20' : ''}`}
                      />
                      <span className="font-semibold">No</span>
                    </button>
                  </div>
                  {errors.isRecommended && (
                    <p className="text-red-400 text-sm font-medium animate-in fade-in">
                      {errors.isRecommended.message}
                    </p>
                  )}
                </div>

                {/* Tags */}
                <div className="w-full">
                  <label className="text-xl font-bold text-slate-100 mb-2 block">
                    Additional Tags
                  </label>
                  <p className="text-slate-400 text-sm mb-4">
                    Add descriptive tags separated by commas.
                  </p>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        placeholder="e.g. action-packed, great soundtrack, family friendly..."
                        rows="4"
                        className="w-full bg-slate-900/60 border border-slate-700 text-white placeholder-slate-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 transition-all duration-200 resize-y"
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Error Banner */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-red-500/10 border border-red-500/50 text-red-200 px-6 py-4 rounded-xl text-center flex items-center justify-center gap-2">
                <Info className="w-5 h-5 shrink-0" />
                Please fix the errors above before submitting.
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-center pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center justify-center gap-2 w-full max-w-sm rounded-xl text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all font-bold py-4 shadow-lg shadow-blue-500/20 text-lg"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-6 h-6 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Rating'
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="flex justify-center items-center py-20 min-h-[50vh]">
          <Circles stroke="#3b82f6" fill="#3b82f6" />
        </div>
      )}
    </>
  );
}

export default UserRating;
